<template>
    <div id="goal-plan">
        <template v-if="!!goalPlan && !!patient">
            <div v-if="!flat">
                <div class="tab-block container">
                    <div class="row">
                        <div class="col">
                            <button type="button"
                                    :class="tabClass(0)"
                                    @click="tabClicked(0)">
                                DETAILS
                                <span v-if="showDetailsModified"
                                      class="modified-pill small-pill pull-right-indented">
                                    M
                                </span>
                            </button>
                        </div>
                        <div class="col">
                            <button type="button"
                                    :class="tabClass(1)"
                                    @click="tabClicked(1)">
                                GOALS
                                <span v-if="showGoalsModified"
                                      class="modified-pill small-pill pull-right-indented">
                                    M
                                </span>
                            </button>
                        </div>
                        <div class="col">
                            <button type="button"
                                    :class="tabClass(2)"
                                    @click="tabClicked(2)">
                                SAFETY PLAN
                                <span v-if="showSafetyPlansModified"
                                      class="modified-pill small-pill pull-right-indented">
                                    M
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div id="page-outer"
                 ref="pageouter"
                 :class="outerClass">
                <div v-if="showPanel0"
                     class="page-break">
                    <h1 v-if="flat">
                        Details
                    </h1>
                    <patient-details :key="key"
                                     v-model="goalPlan"
                                     :patient="patient"
                                     :visibility="visibility"
                                     :edit-state="editState"
                                     :flat="flat"
                                     :url-head="urlHead" />
                </div>
                <div v-if="showPanel1"
                     class="page-break">
                    <template v-if="flat">
                        <hr>
                        <h1>Goals</h1>
                    </template>
                    <recovery-goal-types :key="key"
                                         v-model="goalPlan.recoveryGoalTypes"
                                         :patient-id="patient.id"
                                         :goal-plan-id="goalPlan.goalPlanId"
                                         :edit-state="editState"
                                         :review-statuses="reviewStatuses"
                                         :url-head="urlHead"
                                         :flat="flat" />
                </div>
                <div v-if="showPanel2"
                     class="page-break">
                    <template v-if="flat">
                        <hr>
                        <h1>Safety plan</h1>
                        <relapse-prevention-plans-flat :key="key"
                                                       v-model="goalPlan.relapsePreventionPlans"
                                                       :patient-id="patient.id"
                                                       :edit-state="editState"
                                                       :start-page="planStartPage"
                                                       :component-key="componentKey"
                                                       :url-head="urlHead" />
                    </template>
                    <template v-else>
                        <relapse-prevention-plans :key="key"
                                                  v-model="goalPlan.relapsePreventionPlans"
                                                  :patient-id="patient.id"
                                                  :goal-plan-id="goalPlan.goalPlanId"
                                                  :edit-state="editState"
                                                  :start-page="planStartPage"
                                                  :component-key="componentKey"
                                                  :hide-print="hidePrint"
                                                  :url-head="urlHead" />
                    </template>
                </div>
            </div>
            <div v-if="showNotify"
                 class="box-footer">
                <table class="button-table">
                    <tr>
                        <td class="bottom-first">
                            <button type="button"
                                    class="bottom-button button-light full-width"
                                    @click="notifyPressed()">
                                Send patient notification <i class="icon-sent" />
                            </button>
                        </td>
                        <td class="bottom-last">
                            <button type="button"
                                    class="bottom-button button-light full-width"
                                    @click="printPressed()">
                                Print <i class="icon-printer" />
                            </button>
                        </td>
                    </tr>
                </table>
            </div>
        </template>
        <template v-else>
            <div class="loading">
                <img src="/Content/images/loading.gif">
            </div>
        </template>
    </div>

    <!-- Dialog -->
    <div :id="dialogId"
         class="modal fade"
         role="dialog"
         :aria-labelledby="`${dialogId}Title`"
         aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered dialog-small"
             role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <label :id="`${dialogId}Title`"
                           class="modal-title">
                        <i class="icon icon-sent" />
                        Notify patient
                    </label>
                </div>
                <div class="modal-body">
                    <label>Send notification to patient?</label>
                </div>
                <div v-if="otherActiveGoalPlans">
                    <label>
                        <span class="warning"> Warning: </span>
                        <span>This will close the currently open Goal Plan. Do you wish to continue?</span>
                    </label>
                </div>
                <div class="modal-footer left-align">
                    <div class="ms-auto">
                        <button type="button"
                                class="btn btn-color btn-outline-primary btn-sm"
                                @click="notifyCancel">
                            Go Back
                        </button>
                        <update-button button-label="Send"
                                       :sending="sending"
                                       @click="notifyConfirm" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script setup>
    import { defineProps, defineEmits, onMounted, nextTick, computed, ref, inject } from 'vue';
    import axios from 'axios';
    import { axiosError, axiosConfig } from '../../js/AjaxUtilities.js';
    import { modalDialog } from '../../js/ModalDialog.js';

    import patientDetails from './PatientDetails.vue';
    import recoveryGoalTypes from './RecoveryGoalTypes.vue';
    import relapsePreventionPlans from './RelapsePreventionPlans.vue';
    import relapsePreventionPlansFlat from './RelapsePreventionPlansFlat.vue';
    import UpdateButton from '../UpdateButton.vue';


    const props = defineProps({
        modelValue: {
            type: Object,
            default: null
        },
        patient: {
            type: Object,
            default: null
        },
        editState: {
            type: Number,
            default: 0
        },
        reviewStatuses: {
            type: Array,
            default: null
        },
        visibility: {
            type: Object,
            default: null
        },
        urlHead: {
            type: String,
            default: null
        },
        flat: {
            type: Boolean,
            default: false
        },
        justRelapsePreventionPlan: {
            type: Boolean,
            default: false
        },
        hidePrint: {
            type: Boolean,
            default: false
        }
    });

    const emit = defineEmits(['update:modelValue']);
    const currentPanel = inject('currentPanel', ref(0));
    const currentPanelChange = inject('currentPanelChange');

    onMounted(() => heartbeat());


    const goalPlan = computed({
        get: () => props.modelValue,
        set: newValue => emit("update:modelValue", newValue)
    });

    const key = computed(() => `page-key-${componentKey.value}`);
    const dialogId = computed(() => `notify-patient-confirm-${goalPlan.value.goalPlanId}`);

    const showPanel0 = computed(() => showPanel(0, currentPanel.value))
    const showPanel1 = computed(() => showPanel(1, currentPanel.value))
    const showPanel2 = computed(() => showPanel(2, currentPanel.value))
    const showNotify = computed(() => props.editState == 1);
    const outerClass = computed(() => calcOuterClass(props.flat, showNotify.value));
    const showDetailsModified = computed(() => props.editState == 1 && areDetailsModified(goalPlan.value));
    const showGoalsModified = computed(() => props.editState == 1 && areGoalsModified(goalPlan.value));
    const showSafetyPlansModified = computed(() => props.editState == 1 && areSafetyPlansModified(goalPlan.value));

    const minPanelPos = 0;
    const maxPanelPos = 2;
    const componentKey = ref(0);
    const planStartPage = ref(0);
    const sending = ref(false);
    const pageouter = ref(null);
    const otherActiveGoalPlans = ref(false);


    function tabClicked(posVal) {
        if (currentPanelChange) {
            currentPanelChange(posVal);
        } else {
            changeLocalPanelRef(posVal);
        }
        nextTick(() => scrollToTop());
    }

    function changeLocalPanelRef(posVal) {
        currentPanel.value = posVal;
    }

    function scrollToTop() {
        pageouter.value.scrollTop = 0;
    }

    function tabClass(posVal) {
        if (posVal == currentPanel.value) {
            return "highlighted-tab tab-button"
        } else {
            return "unhighlighted-tab tab-button";
        }
    }

    function calcOuterClass(flat, showNotify) {
        if (flat) {
            return "";
        } else if (showNotify) {
            return "scrolling-container-compressed";
        } else {
            return "scrolling-container";
        }
    }

    function showPanel(panelNumber, cur) {
        if (props.justRelapsePreventionPlan) {
            return panelNumber == 2;
        } else {
            return ((panelNumber == cur) || props.flat);
        }
    }

    function notifyPressed() {
        let goalPlanId = goalPlan.value.goalPlanId;
        checkOtherGoalPlansActive(goalPlanId);
    }

    function notifyCancel() {
        modalDialog.closeModal(dialogId.value);
    }

    function notifyConfirm() {
        sending.value = true;

        let goalPlanId = goalPlan.value.goalPlanId;
        axios
            .post(`/GoalPlanNotification/NotifyForGoalPlan`, goalPlanId, axiosConfig())
            .then(function (response) {
                if (!response.data.success) {
                    alert(`Failed to update relapse prevention plan: ${response.data.message}`);
                }
            })
            .catch(function (error) {
                axiosError("Goal plan notification", axios, error);
            })
            .finally(function () {
                modalDialog.closeModal(dialogId.value);
                sending.value = false;
            });
    }

    function checkOtherGoalPlansActive(goalPlanId) {
        axios
            .get(`/GoalPlanNotification/AreOtherGoalPlansActive?goalPlanId=${goalPlanId}`)
            .then(function (response) {
                if (response.data.success) {
                    otherActiveGoalPlans.value = response.data.otherActiveGoalPlans;
                } else {
                    otherActiveGoalPlans.value = true;
                }
                modalDialog.openModal(dialogId.value);
            })
            .catch(function (error) {
                axiosError("Check other goal plans active", axios, error);
            })
            .finally(function () {
            });
    }

    function printPressed() {
        window.open(`/${props.urlHead}/GoalPlanPrintable?goalPlanId=${goalPlan.value.goalPlanId}`, "_blank");
    }

    function areDetailsModified(goalPlan) {
        return goalPlan.relationshipBlocks.some(r => r.modified) ||
            goalPlan.demographicItems.some(d => d.modified);
    }

    function areGoalsModified(goalPlan) {
        return goalPlan.recoveryGoalTypes.some(r => r.recoveryGoalBlocks.some(b => b.itemsModified || b.reviewModified));

    }

    function areSafetyPlansModified(goalPlan) {
        return goalPlan.relapsePreventionPlans.some(r => r.modified);
    }

    function heartbeat() {
        setTimeout(() => callHeartbeat(), 30000); // call every 30 seconds
    }

    function callHeartbeat() {
        axios
            .post(`/${props.urlHead}/Heartbeat`, null, axiosConfig())
            .then(function (response) {
                if (response.data.success) {
                    heartbeat();
                } else {
                    login();
                }
            })
            .catch(function (error) {
                axiosError("Connection check", axios, error);
            })
            .finally(function () {
                modalDialog.closeModal(dialogId.value);
                sending.value = false;
            });
    }

    function login() {
        window.location.href = `/${props.urlHead}/Login?patientGuid=${props.patient.guid}&goalPlanId=${goalPlan.value.goalPlanId}`;
    }

</script>

<style scoped>
    .loading {
        width: 100%;
        text-align: center;
    }

    .tab-block {
        padding-bottom: var(--heading-spacer);
        padding-top: var(--heading-spacer);
        height: var(--tp-block-header);
    }

        .tab-block.container {
            --bs-gutter-x: 0;
            max-width: 100%;
        }

        .tab-block .row {
            margin-left: var(--tab-buttons-lr-spacing);
            margin-right: var(--tab-buttons-lr-spacing);
        }

        .tab-block .col {
            --bs-gutter-x: 0;
        }

    .tab-button {
        width: 100%;
        border-color: var(--grey-button-color);
    }

    .highlighted-tab, .unhighlighted-tab {
        padding: 6px 12px;
        justify-content: center;
        align-items: center;
        border-radius: 4px;
        font-family: Poppins;
        font-size: 12px;
    }

    .highlighted-tab {
        background-color: var(--orange-button-color);
        color: var(--orange-button-text);
    }


    .unhighlighted-tab {
        background-color: var(--grey-button-color);
        color: var(--grey-button-dim);
    }
</style>
